import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react'

import api from 'utils/api'

interface AuthContextType {
  isAuthenticated: boolean
  login: (email: string, otp: string) => Promise<void>
  logout: () => void
  refreshAccessToken: () => Promise<void>
  error: string
  setError: (error: string) => void
}

interface SearchAuthProviderProps {
  children: ReactNode
}

const SearchAuthContext = createContext<AuthContextType | undefined>(undefined)

export const SearchAuthProvider = ({ children }: SearchAuthProviderProps): JSX.Element => {
  const [tokens, setTokens] = useState(() => ({
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  }))
  const [isAuthenticated, setIsAuthenticated] = useState(!!tokens.access)
  const [error, setError] = useState('')

  const login = async (access: string, refresh: string): Promise<void> => {
    localStorage.setItem('access_token', access)
    localStorage.setItem('refresh_token', refresh)
    setTokens({ access, refresh })
    setIsAuthenticated(true)
  }

  const logout = (): void => {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    setTokens({ access: null, refresh: null })
    setIsAuthenticated(false)
  }

  const refreshAccessToken = async (): Promise<void> => {
    const response = await api.post('/api/token/refresh/', {
      refresh: tokens.refresh,
    })
    const { access } = response.data
    localStorage.setItem('access_token', access)
    setTokens((prev) => ({ ...prev, access }))
  }

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 403) {
          logout()
          setError('Session expired. Please log in again.')
        }

        // eslint-disable-next-line @typescript-eslint/return-await
        return Promise.reject(error)
      }
    )

    return () => {
      api.interceptors.response.eject(interceptor)
    }
  }, [])

  return (
    <SearchAuthContext.Provider
      value={{ isAuthenticated, login, logout, refreshAccessToken, error, setError }}
    >
      {children}
    </SearchAuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(SearchAuthContext)
  if (!context) {
    throw new Error('useAuth must be used within a SearchAuthProvider')
  }

  return context
}
